import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Divider, Form, Input, Select, Spin, Tabs, Typography, message } from 'antd'
import { RobotOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'
import { QaBlock, QaNumGrid, QaSwitchList } from './qaSettingsFormBlocks'
import ContextualIndexPanel from './ContextualIndexPanel'
import type { SettingsNumField } from './settingsFormUi'
import './QaSettings.css'

export type QaMode = 'harness' | 'agent'

const QA_PAYLOAD_KEYS = [
  'rag_agent_enabled',
  'rag_harness_agent_code',
  'rag_agentic_agent_code',
  'rag_retrieval_top_k',
  'rag_context_top_k',
  'rag_source_max_docs',
  'rag_harness_max_rounds',
  'rag_retrieval_rewrite_user_turns',
  'rag_generation_history_messages',
  'rag_query_rewrite_temperature',
  'rag_answer_temperature',
  'rag_thinking_temperature',
  'rag_grounding_temperature',
  'rag_agent_max_steps',
  'rag_agent_temperature',
  'rag_hit_curation_enabled',
  'hybrid_use_rrf',
  'search_vector_knn_k',
  'search_vector_max_ranked_docs',
  'contextual_index_enabled',
  'contextual_index_max_chunks',
  'contextual_index_delay_ms',
] as const

const RETRIEVAL_FIELDS: SettingsNumField[] = [
  {
    name: 'rag_retrieval_top_k',
    label: '检索召回切片数',
    extra: 'ES 按相关度返回的切片条数上限（1–200）',
    min: 1,
    max: 200,
  },
  {
    name: 'rag_context_top_k',
    label: '模型上下文切片数',
    extra: '拼进提示词的前 N 条切片，建议 ≤ 检索召回数',
    min: 1,
    max: 200,
  },
  {
    name: 'rag_source_max_docs',
    label: '参考来源文档数上限',
    extra: '同一文档多切片只显示 1 条来源（1–100）',
    min: 1,
    max: 100,
  },
]

const CONTEXT_FIELDS: SettingsNumField[] = [
  {
    name: 'rag_retrieval_rewrite_user_turns',
    label: '检索改写 · 用户轮数',
    extra: '问句改写读取最近 N 条用户消息；0 = 仅本轮（自纠错模式）',
    min: 0,
    max: 20,
  },
  {
    name: 'rag_generation_history_messages',
    label: '生成回答 · 历史条数',
    extra: '写入模型的 user/assistant 消息合计；0 = 不带历史',
    min: 0,
    max: 40,
  },
]

const HARNESS_FIELDS: SettingsNumField[] = [
  {
    name: 'rag_harness_max_rounds',
    label: '最大检索轮次',
    extra: '未命中时扩大检索重试，最多 5 轮',
    min: 1,
    max: 5,
  },
]

const AGENT_FIELDS: SettingsNumField[] = [
  {
    name: 'rag_agent_max_steps',
    label: '最大规划步数',
    extra: '每步 = 规划 + 可选工具调用（1–30）',
    min: 1,
    max: 30,
  },
  {
    name: 'rag_agent_temperature',
    label: '规划温度',
    extra: '工具选择与 reasoning；建议 0',
    min: 0,
    max: 2,
    step: 0.1,
  },
]

const VECTOR_FIELDS: SettingsNumField[] = [
  {
    name: 'search_vector_knn_k',
    label: 'KNN 候选数',
    extra: '向量搜索参与比对的片段数（64–4096）',
    min: 64,
    max: 4096,
  },
  {
    name: 'search_vector_max_ranked_docs',
    label: '文档榜单上限',
    extra: '折叠为文档后的最大篇数（10–500）',
    min: 10,
    max: 500,
  },
]

function qaModeFromEnabled(enabled: boolean): QaMode {
  return enabled ? 'agent' : 'harness'
}

type AgentOption = {
  agent_code: string
  agent_name: string
  description?: string
}

export default function QaSettings() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])

  const qaMode = Form.useWatch('qa_mode', form) as QaMode | undefined
  const isAgent = qaMode === 'agent'
  const isHarness = qaMode === 'harness'

  const harnessAgentCode = Form.useWatch('rag_harness_agent_code', form) as string | undefined
  const agenticAgentCode = Form.useWatch('rag_agentic_agent_code', form) as string | undefined

  const agentSelectOptions = useMemo(
    () =>
      agentOptions.map((a) => ({
        value: a.agent_code,
        label: `${a.agent_name}（${a.agent_code}）`,
      })),
    [agentOptions],
  )

  const boundAgentCode = isHarness ? harnessAgentCode : agenticAgentCode

  const harnessContextFields = useMemo(
    () =>
      CONTEXT_FIELDS.map((f) =>
        f.name === 'rag_retrieval_rewrite_user_turns'
          ? { ...f, disabled: isAgent, extra: isAgent ? 'Agentic 模式由规划模型自行生成检索 query，此项不生效' : f.extra }
          : f,
      ),
    [isAgent],
  )

  const harnessNumFields = useMemo(
    () => HARNESS_FIELDS.map((f) => ({ ...f, disabled: isAgent })),
    [isAgent],
  )

  const agentNumFields = useMemo(
    () => AGENT_FIELDS.map((f) => ({ ...f, disabled: isHarness })),
    [isHarness],
  )

  const harnessTempFields: SettingsNumField[] = useMemo(
    () => [
      {
        name: 'rag_query_rewrite_temperature',
        label: '问句改写',
        extra: '检索前改写；建议 0',
        min: 0,
        max: 2,
        step: 0.1,
        disabled: isAgent,
      },
      {
        name: 'rag_grounding_temperature',
        label: '答案核对',
        extra: '自纠错核对草稿与片段；建议 0～0.2',
        min: 0,
        max: 2,
        step: 0.1,
        disabled: isAgent,
      },
    ],
    [isAgent],
  )

  const commonTempFields: SettingsNumField[] = [
    {
      name: 'rag_answer_temperature',
      label: '结构化回答',
      extra: '最终回答生成；建议 0～0.2',
      min: 0,
      max: 2,
      step: 0.1,
    },
    {
      name: 'rag_thinking_temperature',
      label: '深度思考',
      extra: '用户开启深度思考时的推理阶段',
      min: 0,
      max: 2,
      step: 0.1,
    },
  ]

  const loadAgents = async () => {
    try {
      const r = await adminApi.get<{ items: AgentOption[] }>('/admin/agents/options', {
        params: { active_only: true, page_size: 200 },
      })
      setAgentOptions(r.data.items ?? [])
    } catch {
      setAgentOptions([])
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      const r = await adminApi.get<Record<string, unknown>>('/admin/settings/model')
      const d = r.data
      const agentEnabled = Boolean(d.rag_agent_enabled)
      form.setFieldsValue({
        qa_mode: qaModeFromEnabled(agentEnabled),
        rag_agent_enabled: agentEnabled,
        rag_harness_agent_code: (d.rag_harness_agent_code as string) || 'rag_harness',
        rag_agentic_agent_code: (d.rag_agentic_agent_code as string) || 'rag_agent',
        rag_retrieval_top_k: d.rag_retrieval_top_k ?? 100,
        rag_context_top_k: d.rag_context_top_k ?? 100,
        rag_source_max_docs: d.rag_source_max_docs ?? 8,
        rag_harness_max_rounds: d.rag_harness_max_rounds ?? 3,
        rag_retrieval_rewrite_user_turns: d.rag_retrieval_rewrite_user_turns ?? 2,
        rag_generation_history_messages: d.rag_generation_history_messages ?? 6,
        rag_query_rewrite_temperature: d.rag_query_rewrite_temperature ?? 0,
        rag_answer_temperature: d.rag_answer_temperature ?? 0.3,
        rag_thinking_temperature: d.rag_thinking_temperature ?? 0.4,
        rag_grounding_temperature: d.rag_grounding_temperature ?? 0.1,
        rag_agent_max_steps: d.rag_agent_max_steps ?? 5,
        rag_agent_temperature: d.rag_agent_temperature ?? 0,
        rag_hit_curation_enabled: Boolean(d.rag_hit_curation_enabled),
        hybrid_use_rrf: Boolean(d.hybrid_use_rrf),
        search_vector_knn_k: d.search_vector_knn_k ?? 1024,
        search_vector_max_ranked_docs: d.search_vector_max_ranked_docs ?? 200,
        contextual_index_enabled: Boolean(d.contextual_index_enabled ?? true),
        contextual_index_max_chunks: Number(d.contextual_index_max_chunks ?? 15),
        contextual_index_delay_ms: Number(d.contextual_index_delay_ms ?? 800),
      })
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载问答配置失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAgents()
    void load()
  }, [form])

  const onModeChange = (mode: QaMode) => {
    form.setFieldsValue({
      qa_mode: mode,
      rag_agent_enabled: mode === 'agent',
    })
  }

  const save = async () => {
    if (saving) return
    setSaving(true)
    try {
      const keys = [
        ...RETRIEVAL_FIELDS,
        ...CONTEXT_FIELDS,
        ...HARNESS_FIELDS,
        ...AGENT_FIELDS,
        ...VECTOR_FIELDS,
        ...harnessTempFields,
        ...commonTempFields,
      ].map((f) => f.name)
      await form.validateFields([
        'qa_mode',
        'rag_harness_agent_code',
        'rag_agentic_agent_code',
        ...keys,
        'rag_hit_curation_enabled',
        'hybrid_use_rrf',
        'contextual_index_enabled',
        'contextual_index_max_chunks',
        'contextual_index_delay_ms',
      ])
      const v = form.getFieldsValue(true) as Record<string, unknown>
      const mode = (v.qa_mode as QaMode) || 'harness'
      const payload: Record<string, unknown> = {}
      for (const k of QA_PAYLOAD_KEYS) {
        if (k in v) payload[k] = v[k]
      }
      payload.rag_agent_enabled = mode === 'agent'
      await adminApi.put('/admin/settings/model', payload)
      await load()
      message.success('问答设置已保存')
    } catch (e) {
      const err = e as { message?: string; errorFields?: unknown }
      if (err?.message && !err.errorFields) message.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="qa-settings">
      <PageHeader
        title="问答设置"
        description="配置 AI 对话的检索策略、问答模式与索引语境；两种模式各绑定一个主编排智能体。"
      />

      <Spin spinning={loading}>
        <Form form={form} layout="vertical" className="qa-settings-form">
          <Form.Item name="qa_mode" hidden>
            <Input />
          </Form.Item>

          <Form.Item name={isHarness ? 'rag_agentic_agent_code' : 'rag_harness_agent_code'} hidden>
            <Input />
          </Form.Item>

          <section className="admin-settings-mode-panel">
            <div className="admin-settings-mode-panel-head">
              <Typography.Text strong>问答模式</Typography.Text>
              <Typography.Paragraph type="secondary" className="admin-settings-mode-panel-desc">
                选择一种模式生效；切换后下方参数会自动展示对应项，保存后前台对话立即按新模式运行。
              </Typography.Paragraph>
            </div>
            <div className="admin-settings-mode-cards" role="radiogroup" aria-label="问答模式">
              <button
                type="button"
                className={`admin-settings-mode-card${isHarness ? ' admin-settings-mode-card-active' : ''}`}
                aria-pressed={isHarness}
                onClick={() => onModeChange('harness')}
              >
                <span className="admin-settings-mode-card-badge">稳定</span>
                <span className="admin-settings-mode-card-title">
                  <ThunderboltOutlined style={{ marginRight: 6 }} />
                  自纠错模式
                </span>
                <p className="admin-settings-mode-card-desc">
                  问句改写 → 多轮扩检索 → 答案核对，未命中自动重试，流程固定、结果可预期。
                </p>
              </button>
              <button
                type="button"
                className={`admin-settings-mode-card${isAgent ? ' admin-settings-mode-card-active' : ''}`}
                aria-pressed={isAgent}
                onClick={() => onModeChange('agent')}
              >
                <span className="admin-settings-mode-card-badge">灵活</span>
                <span className="admin-settings-mode-card-title">
                  <RobotOutlined style={{ marginRight: 6 }} />
                  Agentic
                </span>
                <p className="admin-settings-mode-card-desc">
                  模型自主规划何时检索、是否澄清，适合复杂多跳问题。
                </p>
              </button>
            </div>
            <Form.Item
              name={isHarness ? 'rag_harness_agent_code' : 'rag_agentic_agent_code'}
              label="绑定智能体"
              rules={[{ required: true, message: '请选择智能体' }]}
              extra={
                isHarness
                  ? '自纠错流水线各环节由该智能体的子智能体列表决定（默认：问句改写 → 答案核对 → 结构化回答 → 深度思考）。'
                  : 'Agentic 规划与 Supervisor 子智能体调用均使用该智能体；提示词与编排请在智能体编辑页维护。'
              }
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={agentSelectOptions}
                placeholder="选择智能体"
              />
            </Form.Item>
            {boundAgentCode ? (
              <Typography.Paragraph type="secondary" className="qa-settings-mode-tip">
                编辑绑定智能体：
                <Link to="/manage/agents">智能体列表</Link>
                {' · '}
                标识 <Typography.Text code>{boundAgentCode}</Typography.Text>
                {isAgent ? '（可配置 Supervisor 与 invoke_agent 子智能体）' : '（在子智能体中配置各环节）'}
              </Typography.Paragraph>
            ) : null}
          </section>

          <Tabs
            key={qaMode || 'harness'}
            className="qa-settings-tabs"
            defaultActiveKey="mode"
            items={[
              {
                key: 'mode',
                label: isHarness ? '自纠错参数' : 'Agentic 参数',
                children: isHarness ? (
                  <>
                    <QaBlock title="检索轮次" desc="未命中时自动扩大检索并重试。">
                      <QaNumGrid fields={harnessNumFields} />
                    </QaBlock>
                    <Divider className="qa-settings-block-divider" plain>
                      增强
                    </Divider>
                    <QaSwitchList
                      fields={[
                        {
                          name: 'rag_hit_curation_enabled',
                          label: '检索后文档策展',
                          extra: '调用大模型筛除偏题文档，耗时约 15–30 秒',
                          checkedChildren: '开',
                          unCheckedChildren: '关',
                        },
                      ]}
                    />
                  </>
                ) : (
                  <QaBlock title="规划循环" desc="每步包含一次规划与可选的知识库检索。">
                    <QaNumGrid fields={agentNumFields} />
                  </QaBlock>
                ),
              },
              {
                key: 'retrieval',
                label: '检索与上下文',
                children: (
                  <>
                    <QaBlock title="召回与来源" desc="两种模式共用，控制 ES 召回量与参考来源展示。">
                      <QaNumGrid fields={RETRIEVAL_FIELDS} />
                    </QaBlock>
                    <Divider className="qa-settings-block-divider" plain>
                      多轮对话
                    </Divider>
                    <QaNumGrid fields={harnessContextFields} />
                  </>
                ),
              },
              {
                key: 'temp',
                label: '温度',
                children: (
                  <QaBlock title="各步骤随机性" desc="越低越稳定；置灰项在当前模式下不生效。">
                    <QaNumGrid fields={[...harnessTempFields, ...commonTempFields]} />
                  </QaBlock>
                ),
              },
              {
                key: 'index',
                label: '索引语境',
                children: <ContextualIndexPanel disabled={loading} />,
              },
              {
                key: 'search',
                label: '搜索增强',
                children: (
                  <>
                    <QaBlock title="混合检索" desc="影响对话检索与前台「智能搜索」混合模式。">
                      <QaSwitchList
                        fields={[
                          {
                            name: 'hybrid_use_rrf',
                            label: '使用 RRF 融合',
                            extra: 'BM25 + 向量 Reciprocal Rank Fusion；关闭则使用加权融合',
                            checkedChildren: 'RRF',
                            unCheckedChildren: '加权',
                          },
                        ]}
                      />
                    </QaBlock>
                    <Divider className="qa-settings-block-divider" plain>
                      智能搜索 · 向量模式
                    </Divider>
                    <QaNumGrid fields={VECTOR_FIELDS} />
                  </>
                ),
              },
            ]}
          />

          <div className="qa-settings-footer">
            <Typography.Paragraph type="secondary" className="qa-settings-footer-hint">
              含当前 Tab 全部参数（含索引语境）；智能体提示词与编排请在{' '}
              <Link to="/manage/agents">智能体</Link> 维护
            </Typography.Paragraph>
            <Button type="primary" size="large" onClick={() => void save()} loading={saving}>
              保存问答设置
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  )
}
